//@ts-ignore
import memoize from 'lodash.memoize'
import { Group, BufferAttribute, BufferGeometry, Points, ShaderMaterial, type Texture, TextureLoader } from 'three'
import clusterShader from './shaders/cluster-shader'
import { disposeScene } from '~/lib/three-d-viewer/utils'
import { clustersColorMapping } from './drill-label-manager'
import type { ClusterVisibility } from '~/entities/model3d'
import type { DrillDataConfig } from '~/entities/drillHole'
import { groupDataBy } from '~/lib/three-d-resources/utils'
import type { DrillLayerService, ConfigProvider } from './drill-data-set-manager'
import { applyLocalization } from '@/lib/three-d-resources/geometry/localization'
import type { DrillLabelManager } from '~/lib/three-d-resources/drill-label-manager'
import type { CoreImageApiData, DrillLabelApiData, DrillDataSet, ClusterApiData, DrillLayer } from '~/entities/drillLabel'

type Cluster = { clusterId: number; points: Points }

export class DoraClusterManager implements DrillLayerService, ConfigProvider {
  public group: Group
  public clusterMap: Map<number, Cluster[]>
  private clusterApiDataMap: Map<number, ClusterApiData[]>

  constructor(
    private loaderConfig: { uuid: string | null },
    private labelsManager: DrillLabelManager,
    private onLabelsChanged: () => void,
    private onVisibilityChanged?: (payload: ClusterVisibility[], source?: 'user' | 'system') => void,
  ) {
    this.group = new Group()
    this.group.name = 'Dora Clusters'
    this.clusterMap = new Map()
    this.clusterApiDataMap = new Map()
  }

  getLayerListItems(drillDataSetId: number): DrillLayer[] {
    const labels = this.labelsManager.getLabelsForDrillDataSet(drillDataSetId)
    const clusterLayers = this._getClusterLayers(labels.filter(label => 'clusterData' in label))

    return clusterLayers
  }

  setClusterVisibility(clusterId: number, dataSetId: number, visible: boolean) {
    const clusters = this.clusterMap.get(dataSetId)

    if (!clusters) return

    const cloud = clusters.find(c => c.clusterId === clusterId)

    if (!cloud) return

    cloud.points.visible = visible

    this.onLabelsChanged()
    this.onVisibilityChanged?.([{ clusterId, dataSetId, visible } as ClusterVisibility], 'user')
  }

  isClusterVisible(clusterId: number, dataSetId: number) {
    const clusters = this.clusterMap.get(dataSetId)

    if (!clusters) return false

    const cluster = clusters.find(c => c.clusterId === clusterId)

    return cluster ? cluster.points.visible : false
  }

  async createClusterLabels(drillDataSet: DrillDataSet) {
    this.cleanUp()
    await this._createClusters(drillDataSet)

    Array.from(this.clusterMap.values()).forEach(clusters => {
      clusters.forEach(cluster => {
        cluster.points.visible = true
      })
    })

    this.labelsManager
      .getLabelsForDrillDataSet(drillDataSet.data_set_id)
      .filter(label => 'clusterData' in label)
      .forEach(label => {
        this.labelsManager.setVisible(label.id, true)
      })

    this.onLabelsChanged()
  }

  async fetchClusters(drillDataSets: DrillDataSet[]) {
    for (let dataSet of drillDataSets) {
      const clusterData = await this._fetchClusterApiData(dataSet.data_set_id)
      this.clusterApiDataMap.set(dataSet.data_set_id, clusterData)
    }
  }

  getConfigs(sceneConfigs: DrillDataConfig[]): DrillDataConfig[] {
    return sceneConfigs.reduce<DrillDataConfig[]>((acc, val) => {
      if (!this.clusterMap.has(val.data_set_id)) return acc

      const clusters = this.clusterMap.get(val.data_set_id)

      if (!clusters) return acc

      const clusterEntries = new Map(clusters.map(c => [c.clusterId, c.points.visible]))

      return acc.concat({
        ...val,
        clusters: val.clusters ? val.clusters.map(c => ({ ...c, visible: clusterEntries.get(c.cluster_id) })) : [],
      })
    }, [])
  }

  getDefaultClusters(dataSetId: number) {
    const clusterApiData = this.clusterApiDataMap.get(dataSetId)

    if (!clusterApiData) return []

    return clusterApiData.map(cluster => {
      const { id, cluster_id, data_set_id, experiment_id, label_id, name } = cluster

      return {
        id,
        cluster_id,
        data_set_id,
        experiment_id,
        label_id,
        name,
      }
    })
  }

  async setClustersForScene(sceneConfigs: DrillDataConfig[], drillDataSets: DrillDataSet[]) {
    if (!sceneConfigs.some(config => config.clusters?.length)) return

    this.cleanUp()

    if (!sceneConfigs.length) {
      this.cleanUp()
      await Promise.all(drillDataSets.map(dataSet => this._createClusters(dataSet)))
      return
    }

    drillDataSets.forEach(async ({ data_set_id, localization }) => {
      const [defaultClusterData, coreImages] = await Promise.all([
        this._fetchClusterApiData(data_set_id),
        this._fetchCoreImages(data_set_id),
      ])
      const sceneClusters = sceneConfigs.find(config => config.data_set_id === data_set_id)?.clusters ?? []
      const clusterData = defaultClusterData.map((cluster: ClusterApiData) => {
        const sceneCluster = sceneClusters!.find(x => x.id === cluster.id)!
        return { ...cluster, ...sceneCluster }
      })

      if (!clusterData.length) return

      this._setClusterLabels(clusterData, coreImages, localization)
      this.labelsManager.setSceneId(sceneConfigs[0].camera_id as number)

      clusterData.forEach((cluster: ClusterApiData) => {
        this._createClusterCloud(data_set_id, cluster, localization)
      })
    })
  }

  removeClustersForDataSet(dataSetId: number) {
    const clusters = this.clusterMap.get(dataSetId)

    if (!clusters) return

    for (let cluster of clusters) {
      this.group.remove(cluster.points)
      disposeScene(cluster.points)
    }

    this.clusterMap.delete(dataSetId)
  }

  cleanUp() {
    disposeScene(this.group)
    this.group.clear()
    this.clusterMap.clear()
  }

  private async _createClusters({ data_set_id, localization }: DrillDataSet) {
    const [clusterData, coreImages] = await Promise.all([this._fetchClusterApiData(data_set_id), this._fetchCoreImages(data_set_id)])

    if (!clusterData.length) return

    this._setClusterLabels(clusterData, coreImages, localization)

    clusterData.forEach((cluster: ClusterApiData) => {
      this._createClusterCloud(data_set_id, cluster, localization)
    })
  }

  private _setClusterLabels(clusterData: ClusterApiData[], coreImages: CoreImageApiData[], localization: any) {
    const clusterLabels = clusterData.map(({ label, ...cluster }: ClusterApiData) => {
      const clusterLabel = { ...label, clusterData: cluster } as DrillLabelApiData
      const mergedLabel = this._mergeCoreImages(coreImages, clusterLabel)

      return applyLocalization(localization, mergedLabel)
    })

    this.labelsManager.createLabels(clusterData[0].data_set_id, clusterLabels)
  }

  private async getVpsGradient(): Promise<Texture> {
    const loader = new TextureLoader()
    const texture = await loader.load('/images/VPSgradient.png')
    return texture
  }

  private async _createClusterCloud(dataSetId: number, clusterApiData: ClusterApiData, localization: any) {
    const gradientTexture = await this.getVpsGradient()
    const positionAttribute = new BufferAttribute(new Float32Array(clusterApiData.points.length * 3), 3)
    const vpsAttribute = new BufferAttribute(new Float32Array(clusterApiData.points.length * 3), 3)
    const geometry = new BufferGeometry()
    const material = new ShaderMaterial({ ...clusterShader, uniforms: { uGradientTexture: { value: gradientTexture } } })

    geometry.setAttribute('position', positionAttribute)
    geometry.setAttribute('vpsScore', vpsAttribute)

    const points = new Points(geometry, material)

    for (let i = 0; i < clusterApiData.points.length; i++) {
      const vpsScore = clusterApiData.vps[i] ?? 0
      vpsAttribute.setX(i, vpsScore)

      const x = clusterApiData.points[i][0] - localization.easting
      const y = clusterApiData.points[i][2]
      const z = clusterApiData.points[i][1] - localization.northing

      positionAttribute.setXYZ(i, x, y, -z)
    }
    points.geometry.setDrawRange(0, clusterApiData.points.length)
    points.visible = !!clusterApiData.visible

    positionAttribute.needsUpdate = true
    vpsAttribute.needsUpdate = true
    points.geometry.computeBoundingBox()
    points.geometry.computeBoundingSphere()

    const existingClusters = this.clusterMap.get(dataSetId) || []
    this.clusterMap.set(dataSetId, [...existingClusters, { clusterId: clusterApiData.cluster_id, points: points }])
    this.group.add(points)
  }

  private _mergeCoreImages(coreImages: CoreImageApiData[], l: DrillLabelApiData) {
    const coreImage = coreImages.filter(i => i.labels.includes(l.id))

    return {
      ...l,
      coreImages: ([] as CoreImageApiData[]).concat(coreImage),
    }
  }

  private _fetchClusterApiData = memoize(async (dataSetId: number): Promise<ClusterApiData[]> => {
    return $fetch<ClusterApiData[]>(`/v2/drill/data/${dataSetId}/exported_target_data`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
      params: { include: ['label'] },
    })
  })

  private _fetchCoreImages = memoize(async (dataSetId: number): Promise<CoreImageApiData[]> => {
    return $fetch<CoreImageApiData[]>(`/v2/drill/data/${dataSetId}/core_images`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
    })
  })

  private _getClusterLayers(labels: DrillLabelApiData[]): DrillLayer[] {
    const clusters = groupDataBy(item => item.clusterData!.cluster_id, labels)

    return Object.entries(clusters).map(([id, labels]) => {
      const clusterId = labels[0].clusterData!.cluster_id as number
      const files = groupDataBy(item => item.clusterData!.cluster_id, labels)
      const group = Object.entries(files).map(([fileId, labels]) => {
        const shapLabels = labels.map(label => ({
          id: label.id,
          title: label.collapsed_intercept_info,
          holeId: label.hole_id,
          clusterId: label.clusterData!.cluster_id,
          visible: this.labelsManager.isVisible(label.id),
        }))

        return {
          fileId,
          title: clustersColorMapping[clusterId].name,
          visible: shapLabels.some(l => l.visible),
          labels: shapLabels,
        }
      })

      return {
        id: Number(id),
        type: 'cluster',
        dataSetId: labels[0].data_set_id,
        title: clustersColorMapping[clusterId].name,
        visible: this.isClusterVisible(clusterId, labels[0].data_set_id),
        files: group,
      }
    })
  }
}
